/* eslint-disable no-unused-vars */
import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import apiClient from "../utils/ApiClient/apiClientAxios";

/**
 * Fetches three dashboard endpoints independently.
 * Returns data + per-request loading and error flags.
 * Includes "See More" functions to fetch expanded data.
 */
export const useFetchDashboardData = () => {
  const [recentCases, setRecentCases] = useState([]);
  const [activeAssignments, setActiveAssignments] = useState([]);
  const [recentlyMergedUsers, setRecentlyMergedUsers] = useState([]);

  const [loadingCases, setLoadingCases] = useState(true);
  const [loadingAssignments, setLoadingAssignments] = useState(true);
  const [loadingMerges, setLoadingMerges] = useState(true);

  const [errorCases, setErrorCases] = useState(null);
  const [errorAssignments, setErrorAssignments] = useState(null);
  const [errorMerges, setErrorMerges] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    // Recent cases
    setLoadingCases(true);
    setErrorCases(null);
    apiClient
      .get("/getRecentUserCases", { signal })
      .then((res) => setRecentCases(res.data))
      .catch((err) => {
        if (!axios.isCancel(err)) setErrorCases(err);
      })
      .finally(() => setLoadingCases(false));

    // Active assignments
    setLoadingAssignments(true);
    setErrorAssignments(null);
    apiClient
      .get("/getActiveAssignments", { signal })
      .then((res) => setActiveAssignments(res.data))
      .catch((err) => {
        if (!axios.isCancel(err)) setErrorAssignments(err);
      })
      .finally(() => setLoadingAssignments(false));

    // Recently merged users
    setLoadingMerges(true);
    setErrorMerges(null);
    apiClient
      .get("/getRecentUserMerges", { signal })
      .then((res) => setRecentlyMergedUsers(res.data))
      .catch((err) => {
        if (!axios.isCancel(err)) setErrorMerges(err);
      })
      .finally(() => setLoadingMerges(false));

    return () => {
      controller.abort();
    };
  }, []);

  // See More function for recent cases
  const fetchMoreCases = useCallback(async () => {
    setLoadingCases(true);
    setErrorCases(null);
    try {
      const res = await apiClient.get("/getRecentUserCases?expanded=true");
      setRecentCases(res.data);
    } catch (err) {
      if (!axios.isCancel(err)) setErrorCases(err);
    } finally {
      setLoadingCases(false);
    }
  }, []);

  // See More function for active assignments
  const fetchMoreAssignments = useCallback(async () => {
    setLoadingAssignments(true);
    setErrorAssignments(null);
    try {
      const res = await apiClient.get("/getActiveAssignments?expanded=true");
      setActiveAssignments(res.data);
    } catch (err) {
      if (!axios.isCancel(err)) setErrorAssignments(err);
    } finally {
      setLoadingAssignments(false);
    }
  }, []);

  // See More function for recently merged users
  const fetchMoreMerges = useCallback(async () => {
    setLoadingMerges(true);
    setErrorMerges(null);
    try {
      const res = await apiClient.get("/getRecentUserMerges?expanded=true");
      setRecentlyMergedUsers(res.data);
    } catch (err) {
      if (!axios.isCancel(err)) setErrorMerges(err);
    } finally {
      setLoadingMerges(false);
    }
  }, []);

  return {
    recentCases,
    activeAssignments,
    recentlyMergedUsers,

    loadingCases,
    loadingAssignments,
    loadingMerges,

    errorCases,
    errorAssignments,
    errorMerges,

    // See More functions
    fetchMoreCases,
    fetchMoreAssignments,
    fetchMoreMerges,
  };
};
